import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import uniqBy from "lodash.uniqby";
import { useSelector } from "react-redux";

import type {
  Channel,
  ChannelFilters,
  ChannelOptions,
  ChannelSort,
  StreamChat,
} from "stream-chat";
import { ChannelsQueryState } from "stream-chat-react/dist/components/Chat/hooks/useChannelsQueryState";
import { DefaultStreamChatGenerics, useChatContext } from "stream-chat-react";
import { RootState } from "store/store";

const RECOVER_LOADED_CHANNELS_THROTTLE_INTERVAL_IN_MS = 5000;
const MIN_RECOVER_LOADED_CHANNELS_THROTTLE_INTERVAL_IN_MS = 2000;
const CHANNEL_LIST_MESSAGE_LIMIT = 1;
const DEFAULT_MEMBER_LIMIT = 30;
const MAX_QUERY_CHANNELS_LIMIT = 30;

type AllowedQueryType = Extract<
  ChannelsQueryState["queryInProgress"],
  "reload" | "load-more"
>;

export type CustomQueryChannelParams<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = {
  currentChannels: Array<Channel<StreamChatGenerics>>;
  queryType: AllowedQueryType;
  setChannels: React.Dispatch<
    React.SetStateAction<Array<Channel<StreamChatGenerics>>>
  >;
  setHasNextPage: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CustomQueryChannelsFn<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = (params: CustomQueryChannelParams<StreamChatGenerics>) => Promise<void>;

export const usePaginatedChannels = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  client: StreamChat<StreamChatGenerics>,
  filters: ChannelFilters<StreamChatGenerics>,
  sort: ChannelSort<StreamChatGenerics>,
  options: ChannelOptions,
  activeChannelHandler: (
    channels: Array<Channel<StreamChatGenerics>>,
    setChannels: React.Dispatch<
      React.SetStateAction<Array<Channel<StreamChatGenerics>>>
    >
  ) => void,
  recoveryThrottleIntervalMs: number = RECOVER_LOADED_CHANNELS_THROTTLE_INTERVAL_IN_MS,
  customQueryChannels?: CustomQueryChannelsFn<StreamChatGenerics>,
  onChannelUpdated?: (
    setChannels: React.Dispatch<
      React.SetStateAction<Array<Channel<DefaultStreamChatGenerics>>>
    >
  ) => void
) => {
  const {
    channelsQueryState: { error, setError },
  } = useChatContext("usePaginatedChannels");
  const channelListRefetchTrigger = useSelector(
    (state: RootState) => state.chat.channelListRefetchTrigger
  );
  const [channels, setChannels] = useState<Array<Channel<StreamChatGenerics>>>(
    []
  );
  const [hasNextPage, setHasNextPage] = useState(true);
  const [queryInProgress, setQueryInProgress] =
    useState<AllowedQueryType | null>("reload");
  const lastRecoveryTimestamp = useRef<number | undefined>(undefined);
  const isQueryInFlight = useRef(false);

  const recoveryThrottleInterval =
    recoveryThrottleIntervalMs <
    MIN_RECOVER_LOADED_CHANNELS_THROTTLE_INTERVAL_IN_MS
      ? MIN_RECOVER_LOADED_CHANNELS_THROTTLE_INTERVAL_IN_MS
      : recoveryThrottleIntervalMs ??
        RECOVER_LOADED_CHANNELS_THROTTLE_INTERVAL_IN_MS;
  // memoize props
  const filterString = useMemo(() => JSON.stringify(filters), [filters]);
  const sortString = useMemo(() => JSON.stringify(sort), [sort]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryChannels = async (queryType = "load-more") => {
    if (isQueryInFlight.current) {
      return;
    }

    setError(null);
    isQueryInFlight.current = true;

    if (queryType === "reload") {
      setChannels([]);
    }
    setQueryInProgress(queryType as AllowedQueryType);

    try {
      if (customQueryChannels) {
        await customQueryChannels({
          currentChannels: channels,
          queryType: queryType as AllowedQueryType,
          setChannels,
          setHasNextPage,
        });
      } else {
        const offset = queryType === "reload" ? 0 : channels.length;

        const newOptions = {
          limit: options?.limit ?? MAX_QUERY_CHANNELS_LIMIT,
          message_limit: options?.message_limit ?? CHANNEL_LIST_MESSAGE_LIMIT,
          member_limit: options?.member_limit ?? DEFAULT_MEMBER_LIMIT,
          offset,
          ...options,
        };

        const channelQueryResponse = await client.queryChannels(
          filters,
          sort || {},
          newOptions
        );

        const matchingChannels =
          "needs_reply" in filters
            ? channelQueryResponse.filter(
                (channel) =>
                  Boolean(channel.data?.needs_reply) ===
                  Boolean(filters.needs_reply)
              )
            : channelQueryResponse;

        const newChannels =
          queryType === "reload"
            ? matchingChannels
            : uniqBy([...channels, ...matchingChannels], "cid");

        setChannels(newChannels);
        setHasNextPage(channelQueryResponse.length >= newOptions.limit);

        // Set active channel only on load of first page
        if (!offset && activeChannelHandler) {
          activeChannelHandler(newChannels, setChannels);
        }
      }
    } catch (err) {
      console.warn(err);
      setError(err as Error);
    } finally {
      isQueryInFlight.current = false;
      setQueryInProgress(null);
    }
  };

  const throttleRecover = useCallback(() => {
    const now = Date.now();
    const isFirstRecovery = !lastRecoveryTimestamp.current;
    const timeElapsedSinceLastRecoveryMs = lastRecoveryTimestamp.current
      ? now - lastRecoveryTimestamp.current
      : 0;

    if (
      !isFirstRecovery &&
      timeElapsedSinceLastRecoveryMs < recoveryThrottleInterval &&
      !error
    ) {
      return;
    }

    lastRecoveryTimestamp.current = now;
    queryChannels("reload");
  }, [error, queryChannels, recoveryThrottleInterval]);

  const loadNextPage = () => {
    queryChannels();
  };

  useEffect(() => {
    if (!onChannelUpdated || !setChannels) return;

    const handler = (event: any) => {
      onChannelUpdated(
        setChannels as unknown as React.Dispatch<
          React.SetStateAction<Array<Channel<DefaultStreamChatGenerics>>>
        >
      );
    };

    client.on("channel.updated", handler);
    return () => {
      client.off("channel.updated", handler);
    };
  }, [client, onChannelUpdated, setChannels]);

  useEffect(() => {
    if (client.recoverStateOnReconnect) return;
    const { unsubscribe } = client.on("connection.recovered", throttleRecover);

    return () => {
      unsubscribe();
    };
  }, [client, throttleRecover]);

  useEffect(() => {
    queryChannels("reload");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString, channelListRefetchTrigger]);

  return {
    channels,
    hasNextPage,
    loadNextPage,
    setChannels,
    queryInProgress,
  };
};
